import React, { useEffect, useMemo, useState } from "react";
import { RotateCcw, SlidersHorizontal } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import SheetMap from "./SheetMap";
import { MAX_OFFSET_MM, SHEET, buildSheets } from "./labelSheet";

const toText = (n) => String(n).replace(".", ",");

/** "1,5" → 1.5, limitado a ±MAX_OFFSET_MM e arredondado em 0,1 mm */
const parseOffset = (text) => {
  const n = Number(String(text).replace(",", "."));
  if (!Number.isFinite(n)) return 0;
  return Math.round(Math.min(MAX_OFFSET_MM, Math.max(-MAX_OFFSET_MM, n)) * 10) / 10;
};

const inputClass =
  "h-10 w-full rounded-xl border border-border bg-surface-low px-3 text-base text-foreground transition-colors focus:border-primary focus:outline-none focus:ring-[3px] focus:ring-brand-pistachio/25 md:text-sm";

/**
 * "Layout" da folha: posição inicial (aproveitar folha já usada em parte),
 * calibração da impressora em mm e contorno de conferência.
 */
function SheetLayoutDialog({ open, onOpenChange, entries, startAt, layout, onApply }) {
  const [draftStart, setDraftStart] = useState(startAt);
  const [offsetX, setOffsetX] = useState("0");
  const [offsetY, setOffsetY] = useState("0");
  const [showBorders, setShowBorders] = useState(false);

  useEffect(() => {
    if (!open) return;
    setDraftStart(startAt);
    setOffsetX(toText(layout.offsetX));
    setOffsetY(toText(layout.offsetY));
    setShowBorders(layout.showBorders);
  }, [open, startAt, layout]);

  const previewSlots = useMemo(
    () => buildSheets(entries, draftStart).pages[0],
    [entries, draftStart]
  );

  const row = Math.floor(draftStart / SHEET.columns) + 1;
  const col = (draftStart % SHEET.columns) + 1;

  const handleSubmit = (e) => {
    e.preventDefault();
    onApply({
      startAt: draftStart,
      layout: { offsetX: parseOffset(offsetX), offsetY: parseOffset(offsetY), showBorders },
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-md gap-4 overflow-y-auto rounded-2xl p-5">
        <DialogHeader className="flex-row items-center gap-2 space-y-0 pr-8 text-left">
          <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground">
            <SlidersHorizontal className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <DialogTitle className="font-display text-lg text-primary">Layout da folha</DialogTitle>
            <DialogDescription className="text-xs">
              {SHEET.name} · 38,1 × 21,2 mm
            </DialogDescription>
          </div>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4" noValidate>
          {/* Posição inicial */}
          <section className="space-y-2">
            <div className="flex items-end justify-between gap-2">
              <div>
                <h3 className="text-sm font-semibold text-foreground">Começar na etiqueta</h3>
                <p className="text-xs text-muted-foreground">
                  Toque na primeira posição livre de uma folha já usada.
                </p>
              </div>
              {draftStart > 0 && (
                <button
                  type="button"
                  onClick={() => setDraftStart(0)}
                  className="flex flex-shrink-0 items-center gap-1 rounded-full border border-border bg-surface-low px-2.5 py-1 text-xs font-semibold text-primary transition-colors hover:bg-surface-container"
                >
                  <RotateCcw className="h-3 w-3" />
                  Folha nova
                </button>
              )}
            </div>
            <SheetMap
              slots={previewSlots}
              onPick={setDraftStart}
              selected={draftStart}
              className="mx-auto w-full max-w-[240px]"
            />
            <p className="text-center text-xs font-medium text-primary" aria-live="polite">
              Etiqueta {draftStart + 1} (linha {row}, coluna {col})
              {draftStart > 0 &&
                ` · ${draftStart} ${draftStart === 1 ? "posição pulada" : "posições puladas"}`}
            </p>
          </section>

          {/* Calibração */}
          <section className="space-y-2 border-t border-border pt-4">
            <div>
              <h3 className="text-sm font-semibold text-foreground">Calibração da impressora</h3>
              <p className="text-xs text-muted-foreground">
                Desloca toda a impressão (em mm, até ±{MAX_OFFSET_MM}). Positivo move para a direita
                e para baixo.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1.5">
                <Label htmlFor="label-offset-x" className="text-xs font-semibold">
                  Horizontal (mm)
                </Label>
                <input
                  id="label-offset-x"
                  inputMode="decimal"
                  value={offsetX}
                  onChange={(e) => setOffsetX(e.target.value.replace(/[^\d,.-]/g, ""))}
                  onBlur={() => setOffsetX(toText(parseOffset(offsetX)))}
                  className={inputClass}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="label-offset-y" className="text-xs font-semibold">
                  Vertical (mm)
                </Label>
                <input
                  id="label-offset-y"
                  inputMode="decimal"
                  value={offsetY}
                  onChange={(e) => setOffsetY(e.target.value.replace(/[^\d,.-]/g, ""))}
                  onBlur={() => setOffsetY(toText(parseOffset(offsetY)))}
                  className={inputClass}
                />
              </div>
            </div>
            <label
              htmlFor="label-borders"
              className="flex cursor-pointer items-center justify-between gap-3 rounded-xl border border-border bg-surface-low px-3 py-2.5"
            >
              <span>
                <span className="block text-[13px] font-semibold text-foreground">
                  Imprimir contorno das etiquetas
                </span>
                <span className="block text-[11px] text-muted-foreground">
                  Para conferir o alinhamento em papel comum antes de usar a folha.
                </span>
              </span>
              <Switch id="label-borders" checked={showBorders} onCheckedChange={setShowBorders} />
            </label>
            <p className="rounded-lg bg-terra-soft px-3 py-2 text-xs text-terra">
              Imprima em <strong>tamanho real (100%)</strong>, sem “ajustar à página”, para as
              etiquetas caírem no lugar certo.
            </p>
          </section>

          <div className="grid grid-cols-2 gap-2 pt-1">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="h-10 rounded-full bg-surface-container px-4 text-[13px] font-semibold text-foreground transition-colors hover:bg-surface-high"
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="h-10 rounded-full bg-primary px-4 text-[13px] font-semibold text-primary-foreground shadow-sm transition-all hover:bg-brand-dark active:scale-95"
            >
              Aplicar
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default SheetLayoutDialog;
